/**
 * BL-177 Gate 4 — AI Attendant engine helpers.
 *
 * Pure utilities shared by the `ai-attendant-engine` Edge Function (and, in
 * the future, Module 9's "Just Ask" chat bubble and the Driver App's
 * quick-question box — this file is deliberately channel-agnostic, no
 * SMS/email-specific behavior lives here): the Gate 5 "100% capture"
 * checklist state machine, new/returning/VIP client-tier resolution and
 * greeting/autofill (BL-184), the Claude Messages API request builder +
 * reply parser, auto-confirm/escalation eligibility, and retention-sweep
 * eligibility.
 *
 * No Supabase/Anthropic SDK calls live here on purpose — this file is plain
 * Deno- and Node-compatible JS so it can be unit tested with Jest and
 * imported directly by the Deno Edge Functions, mirroring twilioIntake.js /
 * emailIntake.js from Gates 2-3.
 */

import { normalizeTime } from './bookingTime.js';
import { arrivalPickup, detectAirport, flightSettings, formatClock, inferTerminalType, terminalTypeFromText } from './flightPickup.js';
import { normalizeFlightNumber } from './flightNumber.js';
import { reservationChecklistRequired, reservationServiceCode } from './reservationForm.js';

// ---------------------------------------------------------------------------
// Gate 5 capture checklist, revised by BL-177 Step 2p-B.
// ---------------------------------------------------------------------------

// Always required, no exceptions.
export const CAPTURE_ALWAYS_REQUIRED_FIELDS = [
  'service_type',
  'pickup_location',
  'date',
  'time',
  'passenger_count',
];

// Client details are asked for only when they are not already on file (text channels always know the phone).
export const CAPTURE_CONDITIONAL_FIELDS = ['full_name', 'email', 'phone'];

// Branch-dependent fields are promoted to required by the shared reservation rules.
export const CAPTURE_SOFT_ASK_FIELDS = ['flight_info', 'luggage_count', 'dropoff_location', 'airport_code',
  'flight_direction', 'hours', 'booking_for_other', 'roundtrip', 'same_location', 'return_date', 'return_time',
  'return_pickup', 'return_dropoff', 'return_flight_info', 'child_seats', 'extra_stops', 'passenger_name', 'passenger_phone',
  'flight_time', 'return_flight_time'];

export const CLIENT_TIER = {
  NEW: 'new',
  RETURNING: 'returning',
  VIP: 'vip',
};

export const OUTCOME = {
  IN_PROGRESS: 'in_progress',
  RESOLVED: 'resolved',
  AUTO_CONFIRMED: 'auto_confirmed',
  ESCALATED: 'escalated',
  ABANDONED: 'abandoned',
};

const TERMINAL_OUTCOMES = [
  OUTCOME.RESOLVED,
  OUTCOME.AUTO_CONFIRMED,
  OUTCOME.ESCALATED,
  OUTCOME.ABANDONED,
];

/** @typedef {{ required: boolean, captured: boolean, value: unknown, onFile?: boolean, suggested?: boolean, flightDate?: string }} CaptureChecklistEntry */
/** @typedef {Record<string, CaptureChecklistEntry>} CaptureChecklist */

/**
 * Builds the initial capture_checklist object for a new intake session.
 * `onFileFields` names which conditional fields are already known from the
 * matched Module 2 contact/account record.
 */
/** @param {{ onFileFields?: string[] }} [options] @returns {CaptureChecklist} */
export function buildCaptureChecklist({ onFileFields = [] } = {}) {
  const checklist = {};

  for (const field of CAPTURE_ALWAYS_REQUIRED_FIELDS) {
    checklist[field] = { required: true, captured: false, value: null };
  }
  for (const field of CAPTURE_CONDITIONAL_FIELDS) {
    checklist[field] = {
      required: !onFileFields.includes(field),
      captured: onFileFields.includes(field),
      onFile: onFileFields.includes(field),
      value: null,
    };
  }
  for (const field of CAPTURE_SOFT_ASK_FIELDS) {
    checklist[field] = { required: false, captured: false, value: null };
  }
  checklist.notes = { required: false, captured: false, value: null };

  return applyReservationChecklistRules(checklist);
}

export function applyReservationChecklistRules(checklist = {}) {
  const next = Object.fromEntries(Object.entries(checklist).map(([field, value]) => [field, { ...value }]));
  if (next.service_type?.value) {
    const code = reservationServiceCode(next.service_type.value);
    next.service_type = { ...next.service_type, value: code || next.service_type.value, captured: Boolean(code) };
  }
  const required = reservationChecklistRequired(next);
  for (const field of CAPTURE_CONDITIONAL_FIELDS) {
    if (!next[field]) continue;
    const onFile = next[field].onFile ?? (!next[field].required && next[field].captured);
    next[field] = { ...next[field], onFile, required: next.booking_for_other?.value !== true && !onFile };
  }
  const managed = [...CAPTURE_ALWAYS_REQUIRED_FIELDS, ...CAPTURE_SOFT_ASK_FIELDS];
  for (const field of managed) {
    next[field] = { required: required.includes(field), captured: false, value: null, ...next[field] };
    next[field].required = required.includes(field);
  }
  if (next.service_type?.value === 'AX') {
    if (!['arriving', 'departing'].includes(next.flight_direction.value)) next.flight_direction.captured = false;
    const airports = next.reservation_settings?.value?.airports;
    if (Array.isArray(airports) && !airports.some((airport) => airport.code === next.airport_code.value)) next.airport_code.captured = false;
    for (const field of ['flight_info', 'return_flight_info']) {
      if (next[field].required && !normalizeFlightNumber(next[field].value)) next[field].captured = false;
    }
  }
  if (next.service_type?.value === 'CH') {
    const minimum = Number(next.reservation_settings?.value?.charter_minimum_hours ?? 3);
    if (!Number.isFinite(Number(next.hours.value)) || Number(next.hours.value) < minimum || Number(next.hours.value) > 72) next.hours.captured = false;
  }
  return next;
}

/** Keep saved client details optional and reject legacy serialized pickup defaults for this trip. */
/** @param {CaptureChecklist} [checklist] @param {{ onFileFields?: string[] }} [options] @returns {CaptureChecklist} */
export function ensureClientProvidedBookingFields(checklist = {}, { onFileFields = [] } = {}) {
  const next = Object.fromEntries(Object.entries(checklist).map(([field, entry]) => [field, { ...entry }]));
  for (const field of onFileFields) {
    if (!CAPTURE_CONDITIONAL_FIELDS.includes(field)) continue;
    const entry = next[field] ?? { value: null };
    next[field] = { ...entry, onFile: true, required: false, captured: true };
  }

  const pickup = next.pickup_location;
  if (typeof pickup?.value === 'string') {
    try {
      const parsed = JSON.parse(pickup.value);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        next.pickup_location = { ...pickup, required: true, captured: false, value: null };
      }
    } catch {
      // A normal address supplied in text is not serialized autofill data.
    }
  }
  return applyReservationChecklistRules(next);
}

/**
 * Merges newly-extracted field values (from a Claude pass) into an existing
 * checklist. Only touches keys present (and non-empty/non-null) in
 * `extractedFields`; unknown keys are ignored so a model hallucinating an
 * unrecognised field name cannot corrupt the checklist shape.
 */
/** @param {CaptureChecklist} [checklist] @param {Record<string, unknown>} [extractedFields] @returns {CaptureChecklist} */
export function mergeCaptureChecklist(checklist = {}, extractedFields = {}) {
  const next = {};
  for (const [field, entry] of Object.entries(checklist)) {
    next[field] = { ...entry };
  }

  for (const [field, value] of Object.entries(extractedFields ?? {})) {
    if (!(field in next)) continue;
    if (value === null || value === undefined || value === '') continue;
    next[field] = { ...next[field], captured: true, value,
      ...(['time', 'return_time'].includes(field) ? { suggested: false } : {}) };
  }

  return applyReservationChecklistRules(next);
}

/** True once every required field in the checklist has been captured. */
/** @param {CaptureChecklist} [checklist] */
export function isChecklistComplete(checklist = {}) {
  return missingRequiredFields(checklist).length === 0;
}

/** Names of required fields not yet captured, in checklist declaration order. */
/** @param {CaptureChecklist} [checklist] */
export function missingRequiredFields(checklist = {}) {
  return Object.entries(applyReservationChecklistRules(checklist))
    .filter(([, entry]) => entry?.required && !entry?.captured)
    .map(([field]) => field);
}

// ---------------------------------------------------------------------------
// BL-184 — client-tier resolution, greeting, and address autofill
// ---------------------------------------------------------------------------

/**
 * Resolves the new/returning/VIP client tier. `isVip` (contacts
 * requires_human_review flag, or tier_profiles family = 'vip') always wins;
 * any other matched contact/account record is 'returning'; no match is
 * 'new'.
 */
export function resolveClientTier({ hasMatch = false, isVip = false } = {}) {
  if (isVip) return CLIENT_TIER.VIP;
  if (hasMatch) return CLIENT_TIER.RETURNING;
  return CLIENT_TIER.NEW;
}

/**
 * Builds the tier-appropriate greeting. New clients get a standard
 * greeting with no autofill; returning/VIP greetings acknowledge the
 * return/standing — the existing VIP auto-book ceiling (handled by
 * evaluateAutoConfirmEligibility below) is unrelated to this greeting text.
 */
/** @param {{ tier?: string, contactName?: string }} [options] */
export function buildGreeting({ tier = CLIENT_TIER.NEW, contactName } = {}) {
  const name = typeof contactName === 'string' && contactName.trim() ? contactName.trim() : null;

  if (tier === CLIENT_TIER.VIP) {
    return name
      ? `Welcome back, ${name} — it's great to hear from you again. Let's get your trip arranged.`
      : "Welcome back — it's great to hear from you again. Let's get your trip arranged.";
  }
  if (tier === CLIENT_TIER.RETURNING) {
    return name
      ? `Welcome back, ${name}! Happy to help with your next trip.`
      : 'Welcome back! Happy to help with your next trip.';
  }
  return "Hi! I'd be glad to help set up your trip — let's get a few details.";
}

/**
 * A prior address is never treated as this trip's pickup. It may be
 * suggested conversationally, but the client must provide or confirm it.
 */
/** @param {{ tier?: string, addresses?: unknown[] }} [options] */
export function resolveAutofillAddress() {
  return null;
}

// ---------------------------------------------------------------------------
// Human-handoff / auto-confirm eligibility
// ---------------------------------------------------------------------------

// Deliberately conservative — any of these terms in the inbound text routes
// to a human, per the locked "always for money/safety" handoff trigger.
const MONEY_SAFETY_PATTERN = /\b(refund|chargeback|dispute|accident|injur\w*|police|911|emergency|unsafe|assault|lawsuit|sue|credit\s*card\s*number|wire\s*transfer)\b/i;

/** Detects a money/safety concern in free text, for the always-escalate rule. */
export function detectMoneySafetyConcern(text) {
  if (typeof text !== 'string' || !text.trim()) return false;
  return MONEY_SAFETY_PATTERN.test(text);
}

// D1: only consecutive client turns with no new information count, never
// pass_count or total conversation length. A useful answer resets this cap.
export const DEFAULT_STALLED_INTAKE_TURN_CAP = 8;

/**
 * Decides whether this pass must hand off to a human, and why. Since the
 * Gate 5 Escalated-Intake dispatch-board tab does not exist yet, "handoff"
 * here means outcome=escalated + requires_human_review=true, not a UI
 * action.
 *
 * `noProgressTurns` and `checklistComplete` default to values that never
 * trigger the backstop. Legacy turnCount is accepted but never used.
 */
/** @param {{ needsHuman?: boolean, requiresHumanReview?: boolean, moneyOrSafetyDetected?: boolean, escalationReason?: string | null, turnCount?: number, noProgressTurns?: number, checklistComplete?: boolean, stalledIntakeTurnCap?: number }} [options] */
export function evaluateHandoff({
  needsHuman = false,
  requiresHumanReview = false,
  moneyOrSafetyDetected = false,
  escalationReason = null,
  noProgressTurns = 0,
  checklistComplete = true,
  stalledIntakeTurnCap = DEFAULT_STALLED_INTAKE_TURN_CAP,
} = {}) {
  if (requiresHumanReview) {
    return { shouldEscalate: true, reason: escalationReason ?? 'human_review_flagged' };
  }
  if (moneyOrSafetyDetected) {
    return { shouldEscalate: true, reason: 'money_or_safety' };
  }
  if (needsHuman) {
    return { shouldEscalate: true, reason: escalationReason ?? 'unsure_or_explicit_ask' };
  }
  if (!checklistComplete && Number.isFinite(noProgressTurns) && noProgressTurns >= stalledIntakeTurnCap) {
    return { shouldEscalate: true, reason: 'stalled_intake' };
  }
  return { shouldEscalate: false, reason: null };
}

/**
 * Auto-confirm requires: the checklist fully and cleanly captured, the
 * tenant opted in (auto_confirm_enabled), and no pending human-review flag.
 * The VIP auto-book ceiling is absolute — VIP sessions are never eligible
 * here regardless of checklist completeness (tiering the greeting never
 * relaxes this rule).
 */
export function evaluateAutoConfirmEligibility({
  checklist = {},
  tier = CLIENT_TIER.NEW,
  autoConfirmEnabled = true,
  requiresHumanReview = false,
  clientConfirmed = false,
} = {}) {
  if (tier === CLIENT_TIER.VIP) return false;
  if (requiresHumanReview) return false;
  if (!autoConfirmEnabled) return false;
  return clientConfirmed && isChecklistComplete(checklist);
}

const EXPLICIT_BOOKING_CONFIRMATION = /^(?:yes(?:\s*,?\s*(?:please|confirmed|that'?s (?:right|correct)|looks good|go ahead))?|confirm(?:ed)?|that'?s (?:right|correct)|looks good|correct|go ahead)[.! ]*$/i;

/** Only a direct affirmative to the displayed booking summary counts as confirmation. */
export function isExplicitBookingConfirmation(text) {
  return EXPLICIT_BOOKING_CONFIRMATION.test(String(text ?? '').trim());
}

/** Normalizes relative and month/day dates against the organization's local calendar date. */
export function normalizeBookingDate(value, todayDate) {
  const input = String(value ?? '').trim();
  if (!input || !/^\d{4}-\d{2}-\d{2}$/.test(String(todayDate || ''))) return '';
  const [year, month, day] = todayDate.split('-').map(Number);
  const reference = new Date(Date.UTC(year, month - 1, day));
  const format = (date) => date.toISOString().slice(0, 10);
  const moveDays = (count) => {
    const result = new Date(reference);
    result.setUTCDate(result.getUTCDate() + count);
    return format(result);
  };
  const lower = input.toLowerCase().replace(/[,.]/g, '').replace(/(\d)(st|nd|rd|th)\b/gi, '$1')
    .replace(/^(sun|sunday|mon|monday|tue|tuesday|wed|wednesday|thu|thursday|fri|friday|sat|saturday)\s+/, '')
    .replace(/\s+/g, ' ');
  if (lower === 'today') return format(reference);
  if (lower === 'tomorrow') return moveDays(1);
  const weekdayMatch = lower.match(/^(?:(this|next)\s+)?(sun(?:day)?|mon(?:day)?|tue(?:sday)?|wed(?:nesday)?|thu(?:rsday)?|fri(?:day)?|sat(?:urday)?)$/);
  if (weekdayMatch) {
    const weekday = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'].findIndex((name) => weekdayMatch[2].startsWith(name));
    const delta = (weekday - reference.getUTCDay() + 7) % 7;
    return moveDays(weekdayMatch[1] === 'next' || delta === 0 ? delta + 7 : delta);
  }
  const iso = lower.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (iso) {
    const date = new Date(Date.UTC(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3])));
    return date.getUTCFullYear() === Number(iso[1]) && date.getUTCMonth() + 1 === Number(iso[2]) && date.getUTCDate() === Number(iso[3]) && date >= reference
      ? format(date)
      : '';
  }
  const monthDay = lower.match(/^([a-z]+)\s+(\d{1,2})(?:\s+(\d{4}))?$/);
  if (monthDay) {
    const month = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december']
      .findIndex((name) => name.startsWith(monthDay[1]));
    if (month < 0) return '';
    let date = new Date(Date.UTC(monthDay[3] ? Number(monthDay[3]) : year, month, Number(monthDay[2])));
    if (date.getUTCMonth() !== month || date.getUTCDate() !== Number(monthDay[2])) return '';
    if (!monthDay[3] && date < reference) date = new Date(Date.UTC(year + 1, month, Number(monthDay[2])));
    if (monthDay[3] && date < reference) return '';
    return format(date);
  }
  return '';
}

/** Plain-text details for a client to verify before an automatic reservation is made. */
export const BOOKING_ACTIONS = Object.freeze(['Confirm', 'Change', 'Cancel']);
export const BOOKING_FAILURE_REPLY = 'Thanks — our team will confirm your trip shortly.';

export function bookingAction(text, session = {}) {
  if (session.resolved_at || session.archived_at || !['in_progress', 'auto_confirmed'].includes(session.outcome)
    || ['approved', 'declined', 'resolved', 'closed'].includes(session.approval_status)) return null;
  if (session.capture_checklist?.intent?.value !== 'booking' && !session.confirmation_number
    && !session.capture_checklist?.confirmation?.value) return null;
  const keyword = String(text ?? '').trim().toUpperCase();
  if (keyword === 'YES' && session.capture_checklist?.confirmation?.value === 'pending'
    && !session.capture_checklist?.passenger_name_pending?.value) return 'Confirm';
  if (keyword === 'C') return 'Change';
  if (keyword === 'X') return 'Cancel';
  return null;
}

export function extractedBookingDate(value, { storedDate = '', today = '', latestMessage = '' } = {}) {
  if (/^\d{4}-\d{2}-\d{2}$/.test(storedDate)
    && !/\b(?:today|tomorrow|sun(?:day)?|mon(?:day)?|tue(?:sday)?|wed(?:nesday)?|thu(?:rsday)?|fri(?:day)?|sat(?:urday)?|jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:tember)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\b|\d{4}-\d{2}-\d{2}|\d{1,2}\/\d{1,2}/i.test(latestMessage)) return storedDate;
  return normalizeBookingDate(value, today);
}

export function bookingTimeWindow(value) {
  const normalized = normalizeTime(value);
  if (!normalized) return String(value ?? '');
  const [hour, minute] = normalized.split(':').map(Number);
  const start = hour * 60 + minute;
  const end = (start + 15) % 1440;
  const clock = (total) => `${Math.floor(total / 60) % 12 || 12}:${String(total % 60).padStart(2, '0')}`;
  const period = (total) => total < 720 ? 'AM' : 'PM';
  return `${clock(start)}${period(start) === period(end) ? '' : ` ${period(start)}`}–${clock(end)} ${period(end)}`;
}

export function bookingSummaryLines(checklist = {}, { contactName = '', email = '', phone = '', tier = 'returning' } = {}) {
  const value = (field) => String(checklist[field]?.value ?? '').trim();
  const date = value('date');
  const displayDate = /^\d{4}-\d{2}-\d{2}$/.test(date)
    ? new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }).format(new Date(`${date}T12:00:00Z`))
    : date;
  const airport = /\b(airport|terminal|ATL)\b/i.test(`${value('pickup_location')} ${value('dropoff_location')}`) || Boolean(value('flight_info'));
  return [
    `DATE: ${displayDate}`, `TIME: ${bookingTimeWindow(value('time'))}`,
    `NAME: ${value('passenger_name') || value('full_name') || contactName}`,
    ...(tier === 'new' ? [`EMAIL: ${value('email') || email}`, `TEL: ${value('phone') || phone}`] : []),
    `PASSENGER(S): ${value('passenger_count')}`, `LUGGAGE: ${value('luggage_count')}`,
    ...(airport ? [`FLIGHT INFO: ${value('flight_info') || 'to be provided'}`] : []),
    ...(value('flight_time') ? [`FLIGHT TIME: ${value('flight_time')}`] : []),
    ...['time', 'return_time'].filter((key) => checklist[key]?.suggested)
      .map((key) => `${key === 'time' ? 'PICKUP' : 'RETURN PICKUP'}: ${value(key)}. Suggested - adjust if needed.`),
    `FROM: ${value('pickup_location')}`, `TO: ${value('dropoff_location')}`,
    ...(value('service_type') === 'CH' ? [`CHARTER HOURS: ${value('hours')}`] : []),
    ...(checklist.booking_for_other?.value === true ? [`RIDER MOBILE: ${value('passenger_phone')}`] : []),
    ...(checklist.roundtrip?.value === true ? [
      `RETURN: ${value('return_date')} ${value('return_time')}`,
      checklist.same_location?.value === false ? `RETURN ROUTE: ${value('return_pickup')} to ${value('return_dropoff')}` : 'RETURN ROUTE: reverse of outbound',
      ...(value('return_flight_info') ? [`RETURN FLIGHT: ${value('return_flight_info')}`] : []),
      ...(value('return_flight_time') ? [`RETURN FLIGHT TIME: ${value('return_flight_time')}`] : []),
    ] : []),
    ...(Array.isArray(checklist.extra_stops?.value) && checklist.extra_stops.value.length ? [`EXTRA STOPS: ${checklist.extra_stops.value.join('; ')}`] : []),
    ...(Number(checklist.child_seats?.value) > 0 ? [`CHILD SEATS: ${value('child_seats')}`] : []),
  ];
}

export function buildBookingConfirmationRequest(checklist = {}, options = {}) {
  return ['The Ride: ATL — Please confirm your trip', ...bookingSummaryLines(checklist, options),
    ...(reservationServiceCode(checklist.service_type?.value) ? ["We'll confirm your price."] : []),
    'Reply YES to confirm, C to change'].join('\n');
}

export function buildBookingConfirmation(checklist = {}, { confirmationNumber = '', returnConfirmationNumber = '', engagementId = '', isTest = false, tier = 'returning', phone = '', email = '', contactName = '' } = {}) {
  if (!engagementId || !confirmationNumber) return BOOKING_FAILURE_REPLY;
  return [
    `The Ride: ATL — Trip confirmed${isTest ? ' (TEST)' : ''}`, `Conf # ${confirmationNumber}`,
    ...(returnConfirmationNumber ? [`Return conf # ${returnConfirmationNumber}`] : []),
    ...bookingSummaryLines(checklist, { tier, phone, email, contactName }),
    ...(reservationServiceCode(checklist.service_type?.value) ? ["We'll confirm your price."] : []),
    'Reply C to change, X to cancel',
  ].join('\n');
}

/** A sign-off is not permission to overwrite the client's saved identity. */
export function resolvePassengerIdentity({ text = '', knownName = '', checklist = {}, extractedName = '' } = {}) {
  const explicitPassenger = String(text).match(/(?:\b(?:passenger(?:'s)?(?: name)? is|for passenger)|(?:^|\n)\s*(?:my )?name is)\s+([A-Za-z][A-Za-z '-]{1,60})(?:[.!]|\n|$)/i)?.[1]?.trim();
  if (explicitPassenger) return { passengerName: explicitPassenger, question: '', pendingName: '' };
  const signed = String(text).match(/(?:^|\n)\s*(?:(?:thanks|thank you|regards|sincerely|best)[,!]?\s*\n)\s*([A-Za-z][A-Za-z '-]{1,60})[.!]?\s*$/i)?.[1]?.trim()
    || String(extractedName || '').trim();
  const pending = checklist.passenger_name_pending?.value;
  if (pending && isExplicitBookingConfirmation(text)) return { passengerName: String(pending), question: '', pendingName: '' };
  if (pending && /^(no|nope)[.! ]*$/i.test(String(text).trim())) return { passengerName: '', question: '', pendingName: '' };
  if (!signed || !knownName || signed.toLowerCase() === knownName.toLowerCase() || checklist.passenger_name_asked?.captured) {
    return { passengerName: '', question: '', pendingName: pending || '' };
  }
  return { passengerName: '', question: `Is ${signed} the passenger?`, pendingName: signed };
}

/** Model prose cannot override channel identity, ask for a vehicle class, or announce an uncreated booking. */
/** @param {string} text @param {{ channel?: string, missing?: string[], knownName?: string, passengerName?: string }} [options] */
export function safeAttendantProse(text, { channel = '', missing = [], knownName = '', passengerName = '' } = {}) {
  const reply = stripAttendantMarkdown(text);
  const forbiddenQuestion = /\b(service[- ]?type|vehicle (?:class|type)|what (?:type|kind) of (?:service|vehicle|car))\b/i.test(reply)
    || (isTextChannel(channel) && /\b(?:what|provide|share|need|enter|confirm|send).{0,45}\b(?:phone|mobile|telephone|number to reach)\b/i.test(reply))
    || (channel === 'email' && /\b(?:what|provide|share|need|enter|confirm|send).{0,45}\be-?mail\b/i.test(reply))
    || /\b(?:confirmed|booked|all set|confirmation (?:number|#))\b/i.test(reply)
    || (knownName && passengerName && reply.includes(knownName) && reply.includes(passengerName));
  if (!forbiddenQuestion) return reply;
  const labels = {
    pickup_location: 'pickup address', dropoff_location: 'destination', date: 'trip date', time: 'pickup time',
    passenger_count: 'number of passengers', luggage_count: 'number of bags', full_name: 'name',
    email: 'email address', phone: 'mobile number',
  };
  const wanted = missing.filter((field) => labels[field] && !(isTextChannel(channel) && field === 'phone') && !(channel === 'email' && field === 'email')).slice(0, 2);
  return wanted.length ? `Please share your ${wanted.map((field) => labels[field]).join(' and ')}.` : 'Thanks — I will check the trip details before asking you to confirm.';
}

/** Removes common Markdown formatting from a model-authored message before sending it to clients. */
export function stripAttendantMarkdown(text) {
  return String(text ?? '')
    .replace(/```[\s\S]*?```/g, '')
    .replace(/^\s{0,3}#{1,6}\s+/gm, '')
    .replace(/^\s*[-*+]\s+/gm, '')
    .replace(/^\s*\d+[.)]\s+/gm, '')
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/__(.*?)__/g, '$1')
    .replace(/[*_`]([^*_`]+)[*_`]/g, '$1')
    .trim();
}

// ---------------------------------------------------------------------------
// The Attendant's system prompt (moved here from the Edge Function so a second caller, the staff-triggered
// "Create reservation" extraction in reservation-from-conversation, uses the identical prompt and JSON shape
// instead of a copy that could drift). `extraExtractedFields` appends optional extracted keys to the envelope
// without introducing another intake engine.
// ---------------------------------------------------------------------------

/**
 * @param {{ tier: string, greeting: string, missing: string[], extraExtractedFields?: string[], channel?: string, senderNumber?: string, today?: string, availableServices?: string[], knownClientName?: string, knownClientEmail?: string, checklist?: CaptureChecklist, airportPickup?: string }} options
 */
export function buildAttendantSystemPrompt({ tier, greeting, missing, extraExtractedFields = [], channel = '', senderNumber = '', today = '', knownClientName = '', knownClientEmail = '', checklist = {}, airportPickup = '' }) {
  const extra = [...new Set(['flight_time', 'return_flight_time', ...extraExtractedFields])]
    .filter((field) => field !== 'service_type').map((field) => `, "${field}"?: string`).join('');
  const byText = isTextChannel(channel);
  return [
    'You are the IntelAgent AI Attendant, handling an inbound transportation booking conversation.',
    `Client tier: ${tier}.`,
    `Suggested greeting for this client (use it only if this is the first message of the conversation): "${greeting}"`,
    // BL-177 Step 2o: the AI asked a texter for their phone number (test #4). On a text or WhatsApp thread the number is the sender's.
    byText
      ? `This conversation is happening by ${String(channel).toLowerCase() === 'whatsapp' ? 'WhatsApp' : 'text message'}, so the client's phone number is already known${senderNumber ? ` (${senderNumber})` : ''}. NEVER ask the client for a phone number and never list it as missing.`
      : null,
    String(channel).toLowerCase() === 'email'
      ? "This conversation is by email. The sender's email address is already known. NEVER ask for an email address or list it as missing."
      : null,
    today
      ? `Today is ${today}. Silently resolve a month/day without a year to its next future occurrence (Sep 23 on Oct 22, 2026 is Thu, Sep 23, 2027). Never ask for clarification of that rule. Accept Sep 23rd and 5:00am. Store the ISO date; show the correct weekday in the summary.`
      : null,
    `Saved trip capture (retain these values unless the client changes them): ${JSON.stringify(checklist)}.`,
    knownClientName || knownClientEmail
      ? `Client details already on file: ${knownClientName ? `name "${knownClientName}"` : ''}${knownClientName && knownClientEmail ? ', ' : ''}${knownClientEmail ? `email "${knownClientEmail}"` : ''}. Do not ask for these details again or block confirmation on them. You may include them in the confirmation summary and invite corrections without waiting for a reply.`
      : null,
    'Pickup, drop-off, date/time, and passenger count must be provided or explicitly confirmed by the client for this trip before auto-confirm. Never treat a pickup from a past trip, an address on file, or an assistant suggestion as confirmed; ask the client to confirm it for this trip. If the client confirms a suggested pickup, record that confirmation as pickup_location.',
    'Never invent a city, state, borough, terminal or other address detail. Copy the client address exactly; a street without a city is incomplete. Ask for its city with the other missing items, never assume Brooklyn or the service area. Expand ATL to Hartsfield-Jackson Atlanta International Airport (ATL); include a terminal only when known.',
    'Capture EVERY supplied answer in extracted, including every fact you restate in reply, even if it was supplied on an earlier turn. Never acknowledge an answer without extracting it. Carry-on and checked luggage are both bags: count them, do not ask whether that is all the luggage. Accept "5 pax".',
    'Ask for the service: Airport Transfer (AX), Point-to-Point (P2P), or Charter (CH). These are trip types, never vehicle classes or pricing tiers. Dispatch selects the vehicle. Extract service_type as AX, P2P or CH only.',
    'A different name in a sign-off may be the passenger, not a new client identity. Ask once "Is <name> the passenger?" if unclear. Never greet with two names or overwrite the saved client name.',
    missing.length
      ? `Still missing from the shared reservation checklist: ${missing.filter((field) => !(byText && field === 'phone') && !(channel === 'email' && field === 'email')).join(', ')}. Ask for ALL missing details together in ONE short list, never one question at a time. Restate known details and retain all previously supplied answers. Subsequent replies list only the remaining missing items. Luggage is optional. Do not restart intake.`
      : 'The capture checklist is fully satisfied. State the trip details plainly and ask the client to reply YES before any booking is created. Never treat an earlier or implied affirmative as confirmation.',
    'Airport Transfer from the airport requires airline and flight number, airport, date and requested pickup time. To-airport flight time is optional. Charter requires hours (company minimum, default 3); drop-off is optional. Point-to-Point and Charter allow up to 3 extra stops and child-seat count. Ask whether a return is needed; collect its date/time and flight for airport returns. Never invent addresses or flights.',
    checklist.reservation_settings?.value ? `Company reservation settings: ${JSON.stringify(checklist.reservation_settings.value)}. Use only these airports and this charter minimum.` : null,
    'When booking_for_other is true, collect passenger_name and passenger_phone, not the saved client identity. Extract child_seats as a count and extra_stops as an array of at most three full addresses. Keep outbound and return date/time/flight separate. Never change the outbound details from a labeled return answer.',
    'Optional departure flight times use flight_time and return_flight_time (HH:MM). They are not pickup times. Airport drop-off pickup suggestions subtract the company airport_dropoff_lead_minutes (default 150 minutes) from departure; no routing or traffic math. Label them "Suggested - adjust if needed". Always accept an explicit pickup-time override; never invent another buffer.',
    'Always ask about flight info for an airport arrival: airline and flight number are required. For airport departures, flight time and flight info are optional. Never block a non-airport trip on flight info.',
    // FA-2: the company's airport arrival rule, only on an airport arrival pickup (see airportPickupGuidance)
    airportPickup || null,
    'If the client raises anything about money (refunds, disputes, payment problems) or safety (accidents, injuries, emergencies), or you are unsure how to proceed, or the client explicitly asks for a human, set needs_human=true.',
    'Respond with ONLY a JSON object of this exact shape, no other text: ' +
      '{"reply": string, "extracted": {"service_type"?: "AX"|"P2P"|"CH", "airport_code"?: string, "flight_direction"?: "arriving"|"departing", "hours"?: number, "booking_for_other"?: boolean, "passenger_name"?: string, "passenger_phone"?: string, "child_seats"?: number, "extra_stops"?: string[], "roundtrip"?: boolean, "same_location"?: boolean, "return_date"?: string, "return_time"?: string, "return_pickup"?: string, "return_dropoff"?: string, "return_flight_info"?: string, "pickup_location"?: string, "dropoff_location"?: string, "date"?: string, "time"?: string, "passenger_count"?: number, "luggage_count"?: number, "full_name"?: string, "email"?: string, "phone"?: string, "flight_info"?: string, "notes"?: string' + extra + '}, "needs_human": boolean, "reason": string|null, "intent": "qa"|"booking"}',
  ].filter(Boolean).join('\n');
}

/** Removes model-invented service names before they can satisfy the booking checklist. */
export function restrictServiceType(extracted = {}, availableServices = []) {
  if (!Object.prototype.hasOwnProperty.call(extracted, 'service_type')) return extracted;
  const requested = String(extracted.service_type ?? '').trim();
  const match = availableServices.find((service) => (
    typeof service === 'string' && service.trim().toLowerCase() === requested.toLowerCase()
  ));
  if (!match) {
    const remaining = { ...extracted };
    delete remaining.service_type;
    return remaining;
  }
  return { ...extracted, service_type: match.trim() };
}

/**
 * Converts an empty model result into a customer-safe reply and staff handoff.
 * @param {{ reply?: string, extracted?: Record<string, unknown>, needsHuman?: boolean, reason?: string | null, intent?: string } | null} parsed
 * @returns {{ reply: string, extracted: Record<string, unknown>, needsHuman: boolean, reason: string | null, intent: string }}
 */
export function ensureAttendantReply(parsed) {
  if (typeof parsed?.reply === 'string' && parsed.reply.trim()) return parsed;
  return {
    ...(parsed ?? {}),
    reply: 'Thanks — one moment while I check that.',
    extracted: {},
    needsHuman: true,
    reason: 'empty_model_reply',
    intent: 'qa',
  };
}

/** Text-message channels: the sender's number is the client's number. */
export function isTextChannel(channel) {
  return ['sms', 'mms', 'whatsapp'].includes(String(channel ?? '').toLowerCase());
}

// ---------------------------------------------------------------------------
// BL-177 Step 2o — what the Assistant says once a booking exists (test #4: it went silent after the booking)
// ---------------------------------------------------------------------------

/** Added to the Assistant's booking confirmation, whatever the model wrote: the confirmation number, and the TEST notice in Test status. */
export function bookingConfirmationFooter({ confirmationNumber = '', isTest = false, replyText = '' } = {}) {
  const lines = [];
  if (confirmationNumber && !String(replyText).includes(String(confirmationNumber))) lines.push(`Confirmation number: ${confirmationNumber}`);
  if (isTest) lines.push('This is a TEST booking: the AI Assistant is in Test mode, so no trip has been scheduled.');
  return lines.join('\n');
}

const ACKNOWLEDGEMENT = /^\s*(thanks?( you)?( so much)?|thank you|ok(ay)?|great|perfect|got it|sounds good|awesome|cool|yes|yep|yeah|no|nope|that'?s (all|it)|nothing( else)?|all set|i'?m good)[\s.!,]*$/i;
// A request to alter or cancel the booking, or to book something else: the Assistant does not renegotiate a confirmed trip, a person does.
const CHANGE_REQUEST = /\b(change|cancel|cancell?ed|reschedul\w*|modify|move|different|wrong|incorrect|update|edit|refund|instead|mistake|another|also need|second (trip|ride|car)|additional|one more|new (trip|ride|booking|reservation)|book (a|another)|return (trip|ride)|round[- ]?trip)\b/i;

/** 'ack' = a thank-you or yes/no, 'change' = asks to alter or cancel the booking or to book something else, 'other' = anything else. */
export function classifyPostBookingMessage(text) {
  const body = String(text ?? '').trim();
  if (/^[cx]$/i.test(body)) return 'change';
  if (ACKNOWLEDGEMENT.test(body)) return 'ack';
  if (CHANGE_REQUEST.test(body)) return 'change';
  return 'other';
}

const SUMMARY_FIELDS = [
  ['pickup_location', 'Pickup'], ['dropoff_location', 'Drop-off'], ['date', 'Date'], ['time', 'Time'],
  ['passenger_count', 'Passengers'], ['luggage_count', 'Luggage'], ['flight_info', 'Flight'],
];

/** The booking in plain lines, from the capture checklist the booking was made from. */
export function summarizeBooking(checklist = {}) {
  const lines = [];
  for (const [field, label] of SUMMARY_FIELDS) {
    const entry = checklist?.[field];
    if (!entry?.captured && (entry?.value === undefined || entry?.value === null || entry?.value === '')) continue;
    let value = entry.value;
    if (typeof value === 'string' && value.startsWith('{')) {
      try { const parsed = JSON.parse(value); value = parsed.formatted ?? parsed.address ?? parsed.line1 ?? value; } catch { /* keep the text */ }
    }
    if (value === undefined || value === null || value === '') continue;
    lines.push(`${label}: ${value}`);
  }
  return lines;
}

/**
 * The Assistant's answer to a text that arrives AFTER its booking was made. At minimum: the confirmation number, a summary of the
 * booking, and "anything else?". A thank-you gets a short answer instead of the whole summary again.
 */
export function buildPostBookingReply({ kind = 'other', checklist = {}, confirmationNumber = '', engagementId = '', isTest = false, tier = 'returning', phone = '', email = '', contactName = '' } = {}) {
  if (!engagementId || !confirmationNumber) return BOOKING_FAILURE_REPLY;
  if (kind === 'ack') return `You're welcome! Conf # ${confirmationNumber}${isTest ? ' (TEST)' : ''}. Reply C to change, X to cancel`;
  return buildBookingConfirmation(checklist, { confirmationNumber, engagementId, isTest, tier, phone, email, contactName });
}

// ---------------------------------------------------------------------------
// Claude (Anthropic Messages API) request/response helpers
// ---------------------------------------------------------------------------

// Model IDs updated 2026-09-03 — the prior pinned snapshots
// (claude-3-5-haiku-20241022, retired 2026-02-19; claude-3-5-sonnet-20241022,
// retired 2025-10-28) were returning non-2xx from Anthropic, which this
// function surfaces as a 502. Current per platform.claude.com/docs.
export const CLAUDE_MODEL = {
  // Default handling/Q&A — cheaper, faster.
  HAIKU: 'claude-haiku-4-5-20251001',
  // Booking-drafting — higher-capability, used once a booking looks likely.
  SONNET: 'claude-sonnet-5',
};

/** Haiku for default handling/Q&A, Sonnet for booking-drafting, per the locked Decision Log. */
export function selectClaudeModel(intent) {
  return intent === 'booking' ? CLAUDE_MODEL.SONNET : CLAUDE_MODEL.HAIKU;
}

/**
 * Builds the fetch() request shape for Anthropic's Messages API. Pure
 * builder — the caller performs the actual fetch so this stays testable
 * without a network mock, matching buildTwilioSendRequest / buildGmailSendRequest.
 */
/** @param {{ apiKey?: string, model?: string, system?: string, messages?: Array<{ role: string, content: string }>, maxTokens?: number }} [options] */
export function buildClaudeMessagesRequest({
  apiKey,
  model,
  system,
  messages = [],
  maxTokens = 1024,
} = {}) {
  if (!apiKey) {
    throw new Error('ANTHROPIC_API_KEY is not configured.');
  }
  if (!Array.isArray(messages) || messages.length === 0) {
    throw new Error('Claude request requires at least one message.');
  }

  return {
    url: 'https://api.anthropic.com/v1/messages',
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': apiKey,
      'anthropic-version': '2023-06-01',
    },
    body: JSON.stringify({
      model: model ?? CLAUDE_MODEL.HAIKU,
      max_tokens: maxTokens,
      system: system ?? undefined,
      messages,
    }),
  };
}

/**
 * Strips a markdown code fence (```json ... ``` or ``` ... ```) that a model
 * response is sometimes wrapped in despite the system prompt asking for a
 * raw JSON object only. BL-243 hardening: the match is intentionally NOT
 * anchored to the whole trimmed string — a model that adds any stray
 * content before/after the fence (a leading "Here's the JSON:", a trailing
 * aside) still gets its fenced JSON found and extracted, rather than
 * falling through to parseClaudeAttendantReply's failure path. Returns the
 * trimmed text unchanged when no fence is present, so it is always safe to
 * run before JSON.parse.
 */
export function extractJsonFromModelText(text) {
  if (typeof text !== 'string') return text;
  const trimmed = text.trim();
  const fenceMatch = trimmed.match(/```(?:json)?\s*([\s\S]*?)\s*```/i);
  return fenceMatch ? fenceMatch[1].trim() : trimmed;
}

// ---------------------------------------------------------------------------
// BL-245 — FlightAware AeroAPI flight lookup
//
// Val's direction (2026-09-05): "skip the soft asks...ask questions only to
// fill necessary gaps... We are IntelAgent...Intelligence is what we do!" —
// once a flight designator is in the client's own message, look up the real
// scheduled time (and terminal/gate, when AeroAPI actually returns it — see
// caveat below) instead of asking. Fully optional at every step: no ident
// found, no FlightAware credential configured, or any lookup failure all
// fall back silently to the existing v17 soft-ask prompt behavior — this
// never blocks or breaks a pass.
//
// Open caveat, flagged rather than assumed: AeroAPI's base flight object
// (GET /flights/{ident}) reliably returns scheduled/estimated departure and
// arrival times, but does NOT reliably return gate/terminal — FlightAware's
// own docs place that behind a higher-tier subscription for the prior API
// generation (FlightXML3 required Silver-or-above for include_ex_data), and
// AeroAPI v4's documented base schema doesn't list terminal/gate fields
// either. So: treat any terminal_origin/terminal_destination/gate_* value
// as a bonus when present, never a requirement — the v17 carrier-based
// terminal inference in buildSystemPrompt stays in place as the fallback
// when AeroAPI doesn't supply it.
// ---------------------------------------------------------------------------

// Matches a typical airline flight designator: 2-3 letters/digits for the
// airline code + 1-4 digits for the flight number, optional trailing letter
// (e.g. DL1234, AA100, UA45B, B61900). Deliberately conservative — a missed
// match just falls back to asking the client, same as before this gate; a
// false-positive match is caught downstream by AeroAPI returning no flight.
const FLIGHT_IDENT_PATTERN = /\b([A-Z]{2,3}\d{1,4}[A-Z]?)\b/;
const FLIGHT_IDENT_PATTERN_GLOBAL = new RegExp(FLIGHT_IDENT_PATTERN, 'g');

/**
 * Best-effort extraction of a flight designator from free client text.
 * Kept for any existing single-ident caller; extractFlightIdents (below) is
 * what the engine actually uses as of the 2026-09-05 round-trip fix, since a
 * round-trip message names two flights (outbound + return) in one message.
 */
export function extractFlightIdent(text) {
  if (typeof text !== 'string') return null;
  const match = text.toUpperCase().match(FLIGHT_IDENT_PATTERN);
  return match ? match[1] : null;
}

/**
 * 2026-09-05 — extracts EVERY flight designator in the text, deduped,
 * in the order they appear. A round-trip client message ("flying out
 * Monday DL597, returning Tuesday DL2629") names two flights in one
 * message; extractFlightIdent (singular) only ever found the first one,
 * so the return leg's flight was silently never looked up. Returns [] when
 * none are found — callers should treat that exactly like extractFlightIdent
 * returning null (nothing to look up).
 */
export function extractFlightIdents(text) {
  if (typeof text !== 'string') return [];
  const matches = text.toUpperCase().match(FLIGHT_IDENT_PATTERN_GLOBAL) ?? [];
  return [...new Set(matches)];
}

/**
 * Pure request builder for AeroAPI's GET /flights/{ident}, mirroring
 * buildClaudeMessagesRequest above — the caller performs the actual fetch.
 */
export function buildFlightLookupRequest({ apiKey, ident } = {}) {
  if (!apiKey) {
    throw new Error('FLIGHTAWARE_API_KEY is not configured.');
  }
  if (!ident) {
    throw new Error('Flight lookup requires a flight ident.');
  }
  return {
    url: `https://aeroapi.flightaware.com/aeroapi/flights/${encodeURIComponent(ident)}?max_pages=1`,
    method: 'GET',
    headers: {
      'x-apikey': apiKey,
      Accept: 'application/json',
    },
  };
}

/**
 * Parses AeroAPI's /flights/{ident} response into the fields this engine
 * cares about. Returns null when the response carries no matching flight
 * (unrecognized ident, no scheduled flights in AeroAPI's window, etc.) so
 * the caller can fall back to the soft-ask flow cleanly.
 */
export function parseFlightLookupResponse(responseBody) {
  const flight = Array.isArray(responseBody?.flights) ? responseBody.flights[0] : null;
  if (!flight) return null;

  return {
    ident: flight.ident ?? null,
    scheduledOut: flight.scheduled_out ?? null,
    estimatedOut: flight.estimated_out ?? null,
    scheduledIn: flight.scheduled_in ?? null,
    estimatedIn: flight.estimated_in ?? null,
    originCode: flight.origin?.code ?? flight.origin?.code_iata ?? null,
    destinationCode: flight.destination?.code ?? flight.destination?.code_iata ?? null,
    // Bonus fields only, per the tier caveat above — commonly absent.
    terminalOrigin: flight.terminal_origin ?? null,
    terminalDestination: flight.terminal_destination ?? null,
    gateOrigin: flight.gate_origin ?? null,
    gateDestination: flight.gate_destination ?? null,
  };
}

/**
 * Formats a parsed flight lookup into one plain-language line the system
 * prompt hands to the model as an already-known fact. Returns null for a
 * lookup that resolved to essentially nothing useful (defensive — should
 * not happen given parseFlightLookupResponse's null-flight guard above).
 */
export function formatFlightLookupSummary(lookup) {
  if (!lookup) return null;
  const parts = [];
  parts.push(`Flight ${lookup.ident ?? ''}`.trim());
  if (lookup.originCode && lookup.destinationCode) {
    parts.push(`${lookup.originCode} to ${lookup.destinationCode}`);
  }
  const departure = lookup.estimatedOut ?? lookup.scheduledOut;
  if (departure) parts.push(`departs ${departure}`);
  const arrival = lookup.estimatedIn ?? lookup.scheduledIn;
  if (arrival) parts.push(`arrives ${arrival}`);
  if (lookup.terminalOrigin) parts.push(`origin terminal ${lookup.terminalOrigin}`);
  if (lookup.terminalDestination) parts.push(`destination terminal ${lookup.terminalDestination}`);
  if (parts.length <= 1) return null;
  return parts.join(', ');
}

// BL-243 hardening: a failed JSON parse used to fall back to the raw model
// text verbatim, which — for a fenced-but-unparseable envelope — meant a
// real customer could receive literal ```json syntax as their text message
// (observed once in production data). Anything that still LOOKS like a
// JSON envelope (starts with a fence or an opening brace) after a parse
// failure is never shown to the customer; a generic, human-safe reply goes
// out instead and the session is flagged for a person to check. Genuine
// plain-text replies (the model ignoring the JSON instruction entirely,
// which does not start with `{` or a fence) are unaffected and still pass
// through as before.
const FAILED_JSON_LOOKING_PATTERN = /^\s*(```|\{)/;

/** True when text still resembles an (unparsed) JSON/fenced-JSON envelope. */
export function looksLikeUnparsedJsonEnvelope(text) {
  return typeof text === 'string' && FAILED_JSON_LOOKING_PATTERN.test(text);
}

const SAFE_FALLBACK_REPLY =
  "Thanks for the message — I'm looping in a teammate to make sure this gets handled right, and they'll follow up with you shortly.";

/**
 * Parses a Claude Messages API response. The system prompt instructs Claude
 * to reply with a single JSON envelope ({ reply, extracted, needs_human,
 * reason, intent }); models sometimes wrap that JSON in a ```json fence
 * anyway, so extractJsonFromModelText strips one before parsing. If the
 * model still doesn't comply (or replies with genuine plain text), the
 * whole text block is treated as the reply and no fields are extracted
 * rather than crashing the pass — UNLESS that text still looks like a
 * JSON/fenced envelope that simply failed to parse (or parsed but carried
 * no usable `reply` string), in which case a safe generic reply is used
 * instead and the pass is flagged needs_human, so raw JSON syntax can never
 * reach a real customer.
 */
export function parseClaudeAttendantReply(responseBody) {
  const textBlock = Array.isArray(responseBody?.content)
    ? responseBody.content.find((block) => block?.type === 'text')?.text ?? ''
    : '';

  try {
    const parsed = JSON.parse(extractJsonFromModelText(textBlock));
    const hasUsableReply = typeof parsed.reply === 'string' && parsed.reply.trim().length > 0;
    if (!hasUsableReply) {
      return {
        reply: SAFE_FALLBACK_REPLY,
        extracted: {},
        needsHuman: true,
        reason: 'missing_reply_field',
        intent: 'qa',
      };
    }
    return {
      reply: parsed.reply,
      extracted: parsed.extracted && typeof parsed.extracted === 'object' ? parsed.extracted : {},
      needsHuman: Boolean(parsed.needs_human),
      reason: typeof parsed.reason === 'string' ? parsed.reason : null,
      intent: typeof parsed.intent === 'string' ? parsed.intent : 'qa',
    };
  } catch {
    if (looksLikeUnparsedJsonEnvelope(textBlock)) {
      return {
        reply: SAFE_FALLBACK_REPLY,
        extracted: {},
        needsHuman: true,
        reason: 'unparseable_model_reply',
        intent: 'qa',
      };
    }
    return { reply: textBlock, extracted: {}, needsHuman: false, reason: null, intent: 'qa' };
  }
}

// ---------------------------------------------------------------------------
// Retention enforcement (ai-attendant-retention-sweep)
// ---------------------------------------------------------------------------

/** A session's outcome is terminal once the conversation has resolved one way or another. */
export function isTerminalOutcome(outcome) {
  return TERMINAL_OUTCOMES.includes(outcome);
}

/**
 * Decides whether a session's conversation_log should be purged by the
 * retention sweep. Factory default retention_days = 0 means the log is
 * dropped as soon as the session reaches a terminal outcome; a positive
 * retention_days keeps it for that many days past resolution.
 *
 * @param {{
 *   outcome?: string,
 *   retentionDays?: number,
 *   resolvedAt?: string | Date | null,
 *   now?: string | Date
 * }} [options]
 */
export function shouldPurgeConversationLog({
  outcome,
  retentionDays = 0,
  resolvedAt,
  now = new Date(),
} = {}) {
  if (!isTerminalOutcome(outcome)) return false;
  if (!resolvedAt) return true;

  const resolvedDate = resolvedAt instanceof Date ? resolvedAt : new Date(resolvedAt);
  if (Number.isNaN(resolvedDate.getTime())) return true;

  const nowDate = now instanceof Date ? now : new Date(now);
  const elapsedMs = nowDate.getTime() - resolvedDate.getTime();
  const retentionMs = Math.max(0, retentionDays) * 24 * 60 * 60 * 1000;
  return elapsedMs >= retentionMs;
}

// ---------------------------------------------------------------------------
// FA-2 — airport arrival pickups
//
// When the client is being picked up AT the airport from a flight, the pickup time follows the company's arrival rule
// (default: the flight's SCHEDULED arrival + 30 min Domestic / + 45 min International, rounded up to 5 minutes). The
// engine looks the flight up at most once per flight per conversation (counted against the tenant's FlightAware cap) and
// hands the model one extra system line. Departures and non-airport trips get no new line: the prompt is unchanged.
// ---------------------------------------------------------------------------

/** True when the trip starts at an airport (the client is arriving on a flight). @param {Record<string, any>} [checklist] */
export function isAirportArrivalPickup(checklist = {}) {
  const pickup = String(checklist.pickup_location?.value ?? '');
  const dropoff = String(checklist.dropoff_location?.value ?? '');
  return Boolean(detectAirport(pickup)) && !detectAirport(dropoff);
}

/** The flight the engine should look up for an airport arrival pickup, or null (no lookup, no cost). @param {Record<string, any>} [checklist] */
export function arrivalFlightToLookUp(checklist = {}) {
  if (!isAirportArrivalPickup(checklist)) return null;
  const flightNumber = normalizeFlightNumber(checklist.flight_info?.value);
  const date = String(checklist.date?.value ?? '');
  if (!flightNumber || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;
  const cached = checklist.flight_schedule?.value;
  if (cached && cached.flight_number === flightNumber && cached.date === date) return null;
  return { flightNumber, date };
}

function localDate(iso, timeZone) {
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(iso));
  } catch {
    return String(iso).slice(0, 10);
  }
}

/**
 * @param {any} responseBody
 * @param {{ flightNumber?: string, date?: string, timeZone?: string }} [options]
 * From an AeroAPI /flights/{ident} answer, the flight landing on the trip date (company time zone): its SCHEDULED arrival and
 * a terminal type inferred from where it comes from. Null when none lands that day.
 */
export function scheduledArrivalForDate(responseBody, { flightNumber, date, timeZone = 'America/New_York' } = {}) {
  const flights = Array.isArray(responseBody?.flights) ? responseBody.flights : [];
  const flight = flights.find((item) => item?.scheduled_in && localDate(item.scheduled_in, timeZone) === date && !item.cancelled);
  if (!flight) return null;
  const originIcao = flight.origin?.code_icao || (String(flight.origin?.code || '').length === 4 ? flight.origin.code : null);
  return {
    flight_number: flightNumber,
    date,
    scheduled_time: new Date(flight.scheduled_in).toISOString(),
    terminal_type: inferTerminalType(originIcao),
  };
}

/**
 * @param {{ checklist?: Record<string, any>, flightSchedule?: Record<string, any>|null, settings?: Record<string, any>|null, timeZone?: string }} [options]
 * The one system line for an airport arrival pickup ('' otherwise). With the flight's scheduled arrival known it names the
 * pickup time; without it, it states the rule so the model can apply it to an arrival time the client gives.
 */
export function airportPickupGuidance({ checklist = {}, flightSchedule = null, settings = null, timeZone = 'America/New_York' } = {}) {
  if (!isAirportArrivalPickup(checklist)) return '';
  const rules = flightSettings(settings);
  const rule = `Airport arrival pickup rule (company setting): the pickup time is the flight's SCHEDULED arrival + ${rules.arrival_buffer_domestic_minutes} minutes for a Domestic arrival or + ${rules.arrival_buffer_international_minutes} minutes for an International arrival, rounded up to the next ${rules.pickup_rounding_minutes} minutes. Never use estimated or landed times; dispatch tracks the flight and adjusts if it changes.`;
  const flightNumber = normalizeFlightNumber(checklist.flight_info?.value);
  const known = flightSchedule && flightSchedule.flight_number === flightNumber && flightSchedule.scheduled_time ? flightSchedule : null;
  if (!known) {
    return `${rule} If the client gives the flight's scheduled arrival time instead of a pickup time, suggest the pickup time by this rule. If the client asks for a different pickup time, use the client's time.`;
  }
  const said = terminalTypeFromText(`${checklist.pickup_location?.value ?? ''} ${checklist.notes?.value ?? ''}`);
  const terminal = said || known.terminal_type || null;
  const pickup = arrivalPickup(known.scheduled_time, terminal, rules);
  const arrival = formatClock(known.scheduled_time, timeZone);
  const label = terminal === 'international' ? 'International' : 'Domestic';
  return `${rule} Flight ${flightNumber} is scheduled to arrive at ${arrival} (${label}${terminal ? '' : ', terminal not confirmed'}), so the pickup time for this trip is ${formatClock(pickup, timeZone)}. When you suggest or confirm the pickup time, use ${formatClock(pickup, timeZone)} and say it is based on the flight's scheduled arrival. If the client asks for a different pickup time, use the client's time.`;
}
